import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Heart, MapPin, Building2, MessageCircle, Send, Pencil, Trash2, Check, X, Image as ImageIcon, Search } from 'lucide-react';
import { cn } from '../lib/utils';
import { likeRecognition, unlikeRecognition, getComments, addComment, deleteComment, getUsers, editRecognition, deleteRecognitionByGiver } from '../services/api';
import { GiphyFetch } from '@giphy/js-fetch-api';
import { Grid } from '@giphy/react-components';

const gf = new GiphyFetch('ihUkZni3rNCH5kEJv5eZpDhe9yJTtMnF');
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';

const coreValueColors = {
  // Core Values
  collaborative: 'bg-blue-100 text-blue-700 border-blue-200',
  dedicated: 'bg-green-100 text-green-700 border-green-200',
  entrepreneurial: 'bg-amber-100 text-amber-700 border-amber-200',
  fun: 'bg-pink-100 text-pink-700 border-pink-200',
};

// Fundamentals get a subtle gray style
const getFundamentalStyle = () => 'bg-slate-100 text-slate-700 border-slate-200';

// Chinese translations for badge tooltips
const BADGE_NAMES_CHINESE = {
  // Core Values
  collaborative: 'K2价值观: C - 合作',
  dedicated: 'K2价值观: D - 敬业',
  entrepreneurial: 'K2价值观: E - 创业精神',
  fun: 'K2价值观: F - 乐趣',
  // Fundamentals
  fundamental_1: '基本准则: #1 – 始终，为所应为',
  fundamental_2: '基本准则: #2 – 做一名响应迅速的狂热分子',
  fundamental_3: '基本准则: #3 – 体现出设计思维',
  fundamental_4: '基本准则: #4 – 具备国际思维',
  fundamental_5: '基本准则: #5 – 为自己的工作感到自豪',
  fundamental_6: '基本准则: #6 – 信守承诺',
  fundamental_7: '基本准则: #7 – 解决问题，不指责',
  fundamental_8: '基本准则: #8 – 做出贡献',
  fundamental_9: '基本准则: #9 – 明确要求',
  fundamental_10: '基本准则: #10 – 认真倾听',
  fundamental_11: '基本准则: #11 – 坦诚直言',
  fundamental_12: '基本准则: #12 – 优先思考团队',
  fundamental_13: '基本准则: #13 – 竞争制胜',
  fundamental_14: '基本准则: #14 – 发挥主人翁精神',
  fundamental_15: '基本准则: #15 – 以流程为导向',
  fundamental_16: '基本准则: #16 – 用善意的眼光看待他人',
  fundamental_17: '基本准则: #17 – 坚持不懈地追求进步',
  fundamental_18: '基本准则: #18 – 注重真诚的表扬',
  fundamental_19: '基本准则: #19 – 倡导主人翁思维和行为',
  fundamental_20: '基本准则: #20 – 正向鼓励',
  fundamental_21: '基本准则: #21 – 合作',
  fundamental_22: '基本准则: #22 – 注重细节',
  fundamental_23: '基本准则: #23 – 心存包容',
  fundamental_24: '基本准则: #24 – 成为品牌大使',
  fundamental_25: '基本准则: #25 – 平衡工作和生活',
  fundamental_26: '基本准则: #26 – 欢迎变革',
  fundamental_27: '基本准则: #27 – 想客户所想',
  fundamental_28: '基本准则: #28 – 分享信息',
  fundamental_29: '基本准则: #29 – 未雨绸缪',
  fundamental_30: '基本准则: #30 – 保持乐趣',
};

function getBadgeTooltip(badge, language) {
  if (language === 'zh' && badge.badge_value && BADGE_NAMES_CHINESE[badge.badge_value]) {
    return BADGE_NAMES_CHINESE[badge.badge_value];
  }
  return badge.badge_name;
}

function formatDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // If less than 24 hours ago
  if (diffHours < 24 && diffHours >= 0) {
    if (diffHours === 0) {
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Just now';
      return `${diffMins}m ago`;
    }
    return `${diffHours}h ago`;
  }

  // If less than 7 days ago
  if (diffDays < 7 && diffDays >= 0) {
    return `${diffDays}d ago`;
  }

  // Otherwise show date
  const isThisYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(isThisYear ? {} : { year: 'numeric' })
  });
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
}

const MIN_LENGTH_ENGLISH = 140;
const MIN_LENGTH_CHINESE = 50;

function isChineseText(text) {
  const chineseChars = (text.match(/[一-鿿]/g) || []).length;
  return chineseChars > text.length * 0.3;
}

function getMinLength(text) {
  return isChineseText(text) ? MIN_LENGTH_CHINESE : MIN_LENGTH_ENGLISH;
}

export function RecognitionCard({ recognition, currentUser, onChange }) {
  const [liked, setLiked] = useState(recognition.liked_by_me);
  const [likes, setLikes] = useState(recognition.like_count || 0);
  const [loading, setLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentCount, setCommentCount] = useState(recognition.comment_count || 0);
  const [loadingComments, setLoadingComments] = useState(false);
  const [showCommentGif, setShowCommentGif] = useState(false);
  const [commentGif, setCommentGif] = useState(null);
  const [commentGifSearch, setCommentGifSearch] = useState('');
  const [showStandaloneGif, setShowStandaloneGif] = useState(false);
  const [standaloneGifSearch, setStandaloneGifSearch] = useState('');
  const { language } = useLanguage();

  // Get the message in the user's preferred language, falling back to original
  const displayedMessage = language === 'zh'
    ? (recognition.message_chinese || recognition.message)
    : (recognition.message_english || recognition.message);

  // Edit / delete (giver only, single-recipient recognitions only)
  const isOwner = recognition.giver?.id === currentUser?.id;
  const canManage = isOwner && !recognition.isGroup;

  const [editing, setEditing] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [editReceiver, setEditReceiver] = useState(null);
  const [changingRecipient, setChangingRecipient] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const recipientResults = users.filter(u =>
    recipientSearch &&
    u.id !== editReceiver?.id &&
    (u.name.toLowerCase().includes(recipientSearch.toLowerCase()) ||
      (u.department || '').toLowerCase().includes(recipientSearch.toLowerCase()))
  ).slice(0, 5);

  const handleStartEdit = async () => {
    setEditMessage(recognition.message || '');
    setEditReceiver(recognition.receiver ? { ...recognition.receiver } : null);
    setChangingRecipient(false);
    setRecipientSearch('');
    setEditError(null);
    setConfirmingDelete(false);
    setEditing(true);
    if (users.length === 0) {
      try {
        const res = await getUsers();
        setUsers(res.data.filter(u => u.id !== currentUser?.id));
      } catch (err) {
        console.error('Failed to load users:', err);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditError(null);
    setChangingRecipient(false);
    setRecipientSearch('');
  };

  const handleSelectNewRecipient = (user) => {
    setEditReceiver(user);
    setChangingRecipient(false);
    setRecipientSearch('');
  };

  const handleSaveEdit = async () => {
    const trimmed = editMessage.trim();
    const minLength = getMinLength(trimmed);
    if (trimmed.length < minLength) {
      setEditError(language === 'zh'
        ? `留言至少需要 ${minLength} 个字符`
        : `Message must be at least ${minLength} characters`);
      return;
    }
    if (editReceiver && editReceiver.id === currentUser?.id) {
      setEditError(language === 'zh' ? '不能表彰自己' : 'You cannot recognize yourself');
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      await editRecognition(recognition.id, { message: trimmed, receiver_id: editReceiver?.id });
      setEditing(false);
      if (onChange) onChange();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteRecognitionByGiver(recognition.id);
      if (onChange) onChange();
    } catch (err) {
      console.error('Failed to delete recognition:', err);
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const handleLike = async () => {
    if (loading) return;
    setLoading(true);

    try {
      if (liked) {
        const res = await unlikeRecognition(recognition.id);
        setLikes(res.data.like_count);
        setLiked(false);
      } else {
        const res = await likeRecognition(recognition.id);
        setLikes(res.data.like_count);
        setLiked(true);
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleComments = async () => {
    if (!showComments && comments.length === 0) {
      setLoadingComments(true);
      try {
        const res = await getComments(recognition.id);
        setComments(res.data || []);
      } catch (err) {
        console.error('Failed to load comments:', err);
      } finally {
        setLoadingComments(false);
      }
    }
    setShowComments(!showComments);
  };

  const handleAddComment = async (e) => {
    if (e) e.preventDefault();
    if (!commentText.trim() && !commentGif) return;

    try {
      const res = await addComment(recognition.id, commentText.trim() || null, commentGif || null);
      setComments([res.data, ...comments]);
      setCommentCount(commentCount + 1);
      setCommentText('');
      setCommentGif(null);
      setShowCommentGif(false);
      setCommentGifSearch('');
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };

  const handleCommentGifSelect = (gif, e) => {
    e.preventDefault();
    setCommentGif(gif.images.fixed_height.url);
    setShowCommentGif(false);
    setCommentGifSearch('');
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(recognition.id, commentId);
      setComments(comments.filter(c => c.id !== commentId));
      setCommentCount(commentCount - 1);
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handleStandaloneGifSelect = async (gif, e) => {
    e.preventDefault();
    const gifUrl = gif.images.fixed_height.url;
    setShowStandaloneGif(false);
    setStandaloneGifSearch('');
    try {
      const res = await addComment(recognition.id, null, gifUrl);
      setComments([res.data, ...comments]);
      setCommentCount(commentCount + 1);
      if (!showComments) setShowComments(true);
    } catch (err) {
      console.error('Failed to add GIF:', err);
    }
  };

  const tags = recognition.tags || [];
  const values = tags.filter(t => t.tag_type === 'value');
  const fundamentals = tags.filter(t => t.tag_type === 'fundamental');

  return (
    <Card className="overflow-hidden border-border bg-card shadow-sm transition-shadow hover:shadow-md">
      <CardContent className="p-0">
        {/* Header with giver info */}
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <p className="text-sm">
            <span className="font-medium text-foreground">{recognition.giver?.name}</span>
            <span className="text-muted-foreground"> {t('recognized', language)}</span>
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {formatDate(recognition.created_at)}
            </span>
            {canManage && !editing && (
              <>
                <button
                  onClick={handleStartEdit}
                  title={language === 'zh' ? '编辑' : 'Edit'}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setConfirmingDelete(true)}
                  title={language === 'zh' ? '删除' : 'Delete'}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Main content with recipient featured */}
        <div className="p-5">
          {/* Delete confirmation */}
          {confirmingDelete && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-destructive/10 px-3 py-2">
              <span className="text-sm text-destructive">
                {language === 'zh' ? '确定要删除这条表彰吗？' : 'Delete this recognition?'}
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
                  {language === 'zh' ? '取消' : 'Cancel'}
                </Button>
                <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={handleDelete} disabled={deleting}>
                  {deleting ? (language === 'zh' ? '删除中...' : 'Deleting...') : (language === 'zh' ? '删除' : 'Delete')}
                </Button>
              </div>
            </div>
          )}

          {/* Recipient section */}
          {editing ? (
            <div className="mb-4 space-y-2">
              <label className="text-sm font-medium">
                {language === 'zh' ? '表彰对象' : 'Recognizing'}
              </label>
              <div className="flex items-center gap-2">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {getInitials(editReceiver?.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-semibold text-foreground">{editReceiver?.name}</span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setChangingRecipient(!changingRecipient)}
                >
                  {language === 'zh' ? '更改' : 'Change'}
                </Button>
              </div>
              {changingRecipient && (
                <div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={language === 'zh' ? '搜索同事...' : 'Search colleagues...'}
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  {recipientSearch && (
                    <div className="border rounded-lg max-h-40 overflow-y-auto mt-1">
                      {recipientResults.length === 0 ? (
                        <p className="text-sm text-muted-foreground p-3 text-center">
                          {language === 'zh' ? '未找到用户' : 'No users found'}
                        </p>
                      ) : (
                        recipientResults.map((u) => (
                          <button
                            key={u.id}
                            onClick={() => handleSelectNewRecipient(u)}
                            className="w-full flex items-center gap-3 p-2 hover:bg-muted transition-colors text-left"
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarFallback>{getInitials(u.name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium">{u.name}</p>
                              <p className="text-xs text-muted-foreground">{u.department} • {u.location}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : recognition.isGroup ? (
            <div className="mb-4">
              <div className="flex flex-wrap gap-3 mb-2">
                {recognition.receivers.map((receiver, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Avatar className="h-10 w-10 ring-2 ring-primary/20 ring-offset-1">
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {getInitials(receiver?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-semibold text-foreground">{receiver?.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
          <div className="mb-4 flex items-start gap-4">
            <Avatar className="h-14 w-14 ring-2 ring-primary/20 ring-offset-2">
              <AvatarFallback className="bg-primary text-primary-foreground text-lg">
                {getInitials(recognition.receiver?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                {recognition.receiver?.name}
                {recognition.receiver_badges && recognition.receiver_badges.length > 0 && (
                  [...recognition.receiver_badges]
                    // Only show badge types that have visual rendering
                    .filter(b => ['k2_value', 'fundamentally_focused'].includes(b.badge_type))
                    .sort((a, b) => {
                      const typeOrder = { k2_value: 0, fundamentally_focused: 1 };
                      if (a.badge_type !== b.badge_type) {
                        return (typeOrder[a.badge_type] ?? 99) - (typeOrder[b.badge_type] ?? 99);
                      }
                      // Within k2_value, sort by C, D, E, F order
                      if (a.badge_type === 'k2_value') {
                        const order = { collaborative: 0, dedicated: 1, entrepreneurial: 2, fun: 3 };
                        return (order[a.badge_value] ?? 99) - (order[b.badge_value] ?? 99);
                      }
                      // Within fundamentally_focused, sort by number
                      const getNum = (badge) => {
                        let match = badge.badge_value?.match(/#(\d+)/);
                        if (!match) match = badge.badge_name?.match(/#(\d+)/);
                        if (!match) match = badge.badge_value?.match(/fundamental_(\d+)/);
                        return parseInt(match?.[1] || '0', 10);
                      };
                      return getNum(a) - getNum(b);
                    })
                    .map((badge, idx) => {
                      // K2 Value Badge - Poker Chip Style
                      if (badge.badge_type === 'k2_value') {
                        const label = badge.badge_value?.charAt(0).toUpperCase() || '';
                        const colors = {
                          c: '#3b82f6', // blue - collaborative
                          d: '#22c55e', // green - dedicated
                          e: '#f59e0b', // amber - entrepreneurial
                          f: '#ec4899', // pink - fun
                        };
                        const color = colors[badge.badge_value?.charAt(0)] || '#f59e0b';
                        return (
                          <div key={idx} className="relative group inline-flex" style={{ cursor: 'pointer' }}>
                            <svg width="28" height="28" viewBox="0 0 44 44">
                              {/* Outer ring */}
                              <circle cx="22" cy="22" r="20" fill={color} stroke={color} strokeWidth="2" />
                              {/* Notches around edge */}
                              {[...Array(16)].map((_, i) => {
                                const angle = (i * 22.5) * Math.PI / 180;
                                const x1 = 22 + Math.cos(angle) * 17;
                                const y1 = 22 + Math.sin(angle) * 17;
                                const x2 = 22 + Math.cos(angle) * 20;
                                const y2 = 22 + Math.sin(angle) * 20;
                                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="white" strokeWidth="2" />;
                              })}
                              {/* Inner circle */}
                              <circle cx="22" cy="22" r="13" fill={color} stroke="white" strokeWidth="1.5" />
                              {/* Letter */}
                              <text x="22" y="27" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
                                {label}
                              </text>
                            </svg>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50">
                              {getBadgeTooltip(badge, language)}
                            </div>
                          </div>
                        );
                      }
                      // Fundamentally Focused Badge - Shield
                      if (badge.badge_type !== 'fundamentally_focused') {
                        // Skip unknown badge types - don't render anything
                        return null;
                      }
                      let numMatch = badge.badge_value?.match(/#(\d+)/);
                      if (!numMatch) {
                        numMatch = badge.badge_name?.match(/#(\d+)/);
                      }
                      if (!numMatch) {
                        numMatch = badge.badge_value?.match(/fundamental_(\d+)/);
                      }
                      const label = numMatch ? numMatch[1] : '';
                      if (!label) {
                        // Skip if we can't extract a number
                        return null;
                      }
                      return (
                        <div key={idx} className="relative group inline-flex" style={{ cursor: 'pointer' }}>
                          <svg width="28" height="32" viewBox="0 0 28 32">
                            <path
                              d="M14 0 C6 0, 0 2, 0 6 L0 18 C0 24, 14 32, 14 32 C14 32, 28 24, 28 18 L28 6 C28 2, 22 0, 14 0 Z"
                              fill="#f59e0b"
                            />
                            <text x="14" y="18" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
                              {label}
                            </text>
                          </svg>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50">
                            {getBadgeTooltip(badge, language)}
                          </div>
                        </div>
                      );
                    })
                )}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5" />
                  {recognition.receiver?.department || recognition.department}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {recognition.receiver?.location || recognition.location}
                </span>
              </div>
              {recognition.receiver?.manager_name && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('manager', language)}: {recognition.receiver.manager_name}
                </p>
              )}
            </div>
          </div>
          )}

          {/* Core value and fundamental badges */}
          <div className="mb-4 flex flex-wrap gap-2">
            {values.map(tag => (
              <span
                key={tag.id}
                className={cn(
                  'inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium',
                  coreValueColors[tag.name] || 'bg-gray-100 text-gray-700 border-gray-200'
                )}
              >
                {language === 'zh' && tag.display_name_chinese ? tag.display_name_chinese : tag.display_name}
              </span>
            ))}
            {fundamentals.map(tag => (
              <span
                key={tag.id}
                className={cn(
                  'inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium',
                  getFundamentalStyle()
                )}
              >
                {language === 'zh' && tag.display_name_chinese ? tag.display_name_chinese : tag.display_name}
              </span>
            ))}
          </div>

          {/* Message */}
          {editing ? (
            <div className="space-y-2">
              <Textarea
                value={editMessage}
                onChange={(e) => setEditMessage(e.target.value)}
                className="min-h-[100px] resize-none"
              />
              <div className="flex items-center justify-between">
                <p className={cn(
                  'text-xs',
                  editMessage.trim().length < getMinLength(editMessage.trim()) ? 'text-destructive' : 'text-muted-foreground'
                )}>
                  {editMessage.trim().length}/{getMinLength(editMessage.trim())}
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={handleCancelEdit} disabled={saving} className="gap-1">
                    <X className="h-4 w-4" />
                    {language === 'zh' ? '取消' : 'Cancel'}
                  </Button>
                  <Button size="sm" onClick={handleSaveEdit} disabled={saving} className="gap-1">
                    <Check className="h-4 w-4" />
                    {saving ? (language === 'zh' ? '保存中...' : 'Saving...') : (language === 'zh' ? '保存' : 'Save')}
                  </Button>
                </div>
              </div>
              {editError && (
                <p className="text-xs text-destructive">{editError}</p>
              )}
            </div>
          ) : (
            <p className="leading-relaxed text-foreground">
              {displayedMessage}
            </p>
          )}

          {/* GIF */}
          {recognition.gif_url && (
            <img src={recognition.gif_url} alt="GIF" className="rounded-lg max-h-48 mt-2" />
          )}

        </div>

        {/* Footer with like and comment buttons */}
        <div className="flex items-center gap-2 border-t border-border/50 px-5 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLike}
            disabled={loading}
            className={cn(
              'gap-2 transition-colors',
              liked && 'text-primary hover:text-primary/80'
            )}
          >
            <Heart
              className={cn('h-4 w-4 transition-all', liked && 'fill-primary')}
            />
            <span>{likes}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleComments}
            className="gap-2"
          >
            <MessageCircle className="h-4 w-4" />
            <span>{commentCount}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowStandaloneGif(!showStandaloneGif)}
            className="gap-2"
          >
            <ImageIcon className="h-4 w-4" />
          </Button>
        </div>

        {/* Standalone GIF picker */}
        {showStandaloneGif && (
          <div className="border-t border-border/50 px-5 py-3">
            <div className="border rounded-lg p-2 space-y-2">
              <Input
                placeholder={language === 'zh' ? '搜索 GIF...' : 'Search GIFs...'}
                value={standaloneGifSearch}
                onChange={(e) => setStandaloneGifSearch(e.target.value)}
                className="text-sm h-8"
              />
              <div className="h-48 overflow-y-auto rounded">
                <Grid
                  key={standaloneGifSearch}
                  width={400}
                  columns={3}
                  gutter={6}
                  fetchGifs={(offset) =>
                    standaloneGifSearch
                      ? gf.search(standaloneGifSearch, { offset, limit: 9 })
                      : gf.trending({ offset, limit: 9 })
                  }
                  onGifClick={handleStandaloneGifSelect}
                  noLink
                />
              </div>
            </div>
          </div>
        )}

        {/* Comments section */}
        {showComments && (
          <div className="border-t border-border/50 px-5 py-3">
            {loadingComments ? (
              <p className="text-sm text-muted-foreground">{t('loading', language)}</p>
            ) : (
              <>
                {/* Existing comments */}
                {comments.length > 0 && (
                  <div className="space-y-3 mb-3">
                    {comments.map((comment) => (
                      <div key={comment.id} className="flex gap-2">
                        <Avatar className="h-7 w-7 flex-shrink-0">
                          <AvatarFallback className="text-xs bg-secondary">
                            {getInitials(comment.user?.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{comment.user?.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(comment.created_at)}
                            </span>
                            {comment.user?.id === currentUser?.id && (
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-muted-foreground hover:text-destructive transition-colors"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          {comment.content && <p className="text-sm text-foreground">{comment.content}</p>}
                          {comment.gif_url && <img src={comment.gif_url} alt="GIF" className="rounded-lg max-h-32 mt-1" />}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Selected GIF preview */}
                {commentGif && (
                  <div className="relative inline-block mb-2">
                    <img src={commentGif} alt="Selected GIF" className="rounded-lg max-h-24" />
                    <button
                      onClick={() => setCommentGif(null)}
                      className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {/* Add comment form */}
                <form onSubmit={handleAddComment} className="flex gap-2">
                  <Input
                    placeholder={t('addComment', language)}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    className="flex-1 h-9"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCommentGif(!showCommentGif)}
                    className="h-9 px-2"
                  >
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                  <Button type="submit" size="sm" disabled={!commentText.trim() && !commentGif}>
                    <Send className="h-4 w-4" />
                  </Button>
                </form>

                {/* GIF picker for comments */}
                {showCommentGif && (
                  <div className="border rounded-lg p-2 space-y-2 mt-2">
                    <Input
                      placeholder={language === 'zh' ? '搜索 GIF...' : 'Search GIFs...'}
                      value={commentGifSearch}
                      onChange={(e) => setCommentGifSearch(e.target.value)}
                      className="text-sm h-8"
                    />
                    <div className="h-48 overflow-y-auto rounded">
                      <Grid
                        key={commentGifSearch}
                        width={400}
                        columns={3}
                        gutter={6}
                        fetchGifs={(offset) =>
                          commentGifSearch
                            ? gf.search(commentGifSearch, { offset, limit: 9 })
                            : gf.trending({ offset, limit: 9 })
                        }
                        onGifClick={handleCommentGifSelect}
                        noLink
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default RecognitionCard;
